import { doc, collection, onSnapshot, query, where, Timestamp, updateDoc, WriteBatch, writeBatch, or, getDoc, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebaseConfig";
import { Account, FetchState, TransactionSource, TransactionStatus, TransactionType } from "../global/types";
import { useState, useEffect } from "react";
import { CALCULATION_CURRENCY } from "../utils/currencyUtils";
import { isInflowTransaction, isOutflowTransaction } from "@/utils/transactionUtils";
import { removeUndefined } from '@/utils/misc';
import { addLedgerTransaction, updateLedgerTransaction } from './ledgerServices';

/** Creates an account. Pass `accountId` for a deterministic id (e.g. the default cash wallet); omit it to auto-generate one. Resolves with the created account's id. */
export async function addAccount(account: Omit<Account, 'id'>, initialBalance?: number, accountId?: string): Promise<string> {
    const batch = writeBatch(db);
    const accountRef = accountId ? doc(db, "accounts", accountId) : doc(collection(db, "accounts"));
    const { createdAt, updatedAt, lastChecked, ...rest } = account;

    // Ensure timestamps are valid numbers or Timestamps
    const validCreatedAt = typeof createdAt === 'number' && !isNaN(createdAt) ? createdAt : (typeof (createdAt as any)?.toMillis === 'function' ? (createdAt as any).toMillis() : Date.now());
    const validUpdatedAt = typeof updatedAt === 'number' && !isNaN(updatedAt) ? updatedAt : (typeof (updatedAt as any)?.toMillis === 'function' ? (updatedAt as any).toMillis() : Date.now());
    const validLastChecked = typeof lastChecked === 'number' && !isNaN(lastChecked) ? lastChecked : (typeof (lastChecked as any)?.toMillis === 'function' ? (lastChecked as any).toMillis() : Date.now());

    const createdTimestamp = Timestamp.fromMillis(validCreatedAt);

    // Clean rest object of undefined values to prevent Firestore errors
    const safeRest = removeUndefined(rest);

    // 1. Create Account Document
    const hasLedgerSeed = !!initialBalance && initialBalance !== 0;

    batch.set(accountRef, {
        ...safeRest,
        balance: hasLedgerSeed ? 0 : (safeRest.balance ?? 0), // Ledger-seeded accounts start at 0; the transaction below bumps it.
        adjustments: 0,
        createdAt: createdTimestamp,
        updatedAt: Timestamp.fromMillis(validUpdatedAt),
        lastChecked: Timestamp.fromMillis(validLastChecked)
    });

    await batch.commit();

    // 2. Post the Initial Balance Transaction through the trusted write path
    if (hasLedgerSeed) {
        await addLedgerTransaction({
            amount: Number(initialBalance) || 0, // Signed: negative seeds a liability (e.g. a LOAN's amount owed).
            currency: safeRest.currency || CALCULATION_CURRENCY, // Fallback, though Account should have currency
            date: createdTimestamp,
            description: 'Initial Balance',
            type: TransactionType.INITIAL_BALANCE,
            categoryId: 'initial_balance', // System category
            categoryGroup: TransactionType.INCOME, // Broadly income? Or System?
            accountId: accountRef.id,
            userId: rest.userId, // removeUndefined widens safeRest's fields to `| undefined`; userId is always supplied.
            status: TransactionStatus.POSTED,
            source: TransactionSource.SYSTEM, // System generated
        });
    }

    return accountRef.id;
}

/** Amount of the account's one-time INITIAL_BALANCE ledger transaction, or 0 if none exists. */
export async function getInitialBalanceAmount(accountId: string): Promise<number> {
    const q = query(
        collection(db, "ledger_transactions"),
        where("accountId", "==", accountId),
        where("type", "==", TransactionType.INITIAL_BALANCE)
    );
    const snap = await getDocs(q);
    if (snap.empty) return 0;
    return Number(snap.docs[0].data().amount) || 0;
}

/**
 * Creates or updates the account's INITIAL_BALANCE ledger transaction. The Cloud Function
 * reacting to ledger_transactions writes diffs the before/after amountù
 */
export async function setInitialBalanceAmount(accountId: string, userId: string, currency: string, amount: number): Promise<void> {
    const q = query(
        collection(db, "ledger_transactions"),
        where("accountId", "==", accountId),
        where("type", "==", TransactionType.INITIAL_BALANCE)
    );
    const snap = await getDocs(q);
    const signedAmount = Number(amount) || 0; // Signed: negative represents a liability (e.g. a LOAN's amount owed).

    if (!snap.empty) {
        await updateLedgerTransaction(snap.docs[0].id, { amount: signedAmount });
    } else if (signedAmount !== 0) {
        await addLedgerTransaction({
            amount: signedAmount,
            currency: currency || CALCULATION_CURRENCY,
            date: Timestamp.now(),
            description: 'Initial Balance',
            type: TransactionType.INITIAL_BALANCE,
            categoryId: 'initial_balance',
            categoryGroup: TransactionType.INCOME,
            accountId,
            userId,
            status: TransactionStatus.POSTED,
            source: TransactionSource.SYSTEM,
        });
    }
}


const updateAccountFn = httpsCallable<Record<string, unknown>, void>(functions, 'updateAccount');

/** FR-8: Account Currency is immutable after create — routed through a trusted callable that rejects any attempt to change it. */
export async function updateAccount(accountId: string, account: Partial<Account>): Promise<void> {
    const safeAccount = removeUndefined(account);
    await updateAccountFn({ ...safeAccount, id: accountId });
}

export async function deleteAccount(accountId: string): Promise<void> {
    const getOnce = <T = any>(ref: any): Promise<T> =>
        new Promise((resolve, reject) => {
            const unsub = onSnapshot(
                ref,
                (snap: any) => {
                    unsub();
                    resolve(snap);
                },
                (err) => {
                    reject(err);
                }
            );
        });

    try {
        const [
            accountSnap,
            accountTransfersSnap,
            recTransactionSetupsSnap,
            recTransactionsSnap,
            recurringRulesSnap,
            variableTransactionsSnap,
            ledgerTransactionsSnap, // New
        ] = await Promise.all([
            getOnce(doc(db, "accounts", accountId)),
            getOnce(
                query(
                    collection(db, "accountTransfers"),
                    or(where("toAccountId", "==", accountId), where("fromAccountId", "==", accountId))
                )
            ),
            getOnce(query(collection(db, "recTransactionSetups"), where("accountRecOrProId", "==", accountId))),
            getOnce(query(collection(db, "recTransactions"), where("accountId", "==", accountId))),
            getOnce(query(collection(db, "recurringTransactionRules"), where("template.accountId", "==", accountId))),
            getOnce(
                query(
                    collection(db, "variableTransactions"),
                    or(where("accountProvenanceId", "==", accountId), where("accountReceivableId", "==", accountId))
                )
            ),
            getOnce(query(collection(db, "ledger_transactions"), where("accountId", "==", accountId))), // New
        ]);

        const accountsArchiveRef = collection(db, "accountsArchive");
        const accountTransfersArchiveRef = collection(db, "accountTransfersArchive");
        const recTransactionSetupsArchiveRef = collection(db, "recTransactionSetupsArchive");
        const recTransactionsArchiveRef = collection(db, "recTransactionsArchive");
        const recurringRulesArchiveRef = collection(db, "recurringTransactionRulesArchive");
        const variableTransactionsArchiveRef = collection(db, "variableTransactionsArchive");
        const ledgerTransactionsArchiveRef = collection(db, "ledgerTransactionsArchive"); // New

        const batches: WriteBatch[] = [];
        let currentBatch = writeBatch(db);
        let opCount = 0;
        const pushOp = (op: () => void) => {
            if (opCount >= 490) { // 490 as safe threshold
                batches.push(currentBatch);
                currentBatch = writeBatch(db);
                opCount = 0;
            }
            op();
            opCount++;
        };

        const now = Timestamp.now();

        // Archive account document (if exists) and delete original
        if (accountSnap.exists && accountSnap.exists()) {
            const accountData = { ...(accountSnap.data() as object), id: accountSnap.id, archivedAt: now };
            const archiveDocRef = doc(accountsArchiveRef);
            pushOp(() => currentBatch.set(archiveDocRef, accountData));
            pushOp(() => currentBatch.delete(doc(db, "accounts", accountId)));
        }

        const archiveDocsFromSnapshot = (snap: any, archiveColRef: any, origCollectionName: string) => {
            if (!snap || !snap.docs) return;
            for (const d of snap.docs) {
                const dataWithMeta = { ...(d.data() as object), id: d.id, originalCollection: origCollectionName, archivedAt: now };
                const archiveDocRef = doc(archiveColRef);
                const origDocRef = doc(db, origCollectionName, d.id);
                pushOp(() => currentBatch.set(archiveDocRef, dataWithMeta));
                pushOp(() => currentBatch.delete(origDocRef));
            }
        };

        archiveDocsFromSnapshot(accountTransfersSnap, accountTransfersArchiveRef, "accountTransfers");
        archiveDocsFromSnapshot(recTransactionSetupsSnap, recTransactionSetupsArchiveRef, "recTransactionSetups");
        archiveDocsFromSnapshot(recTransactionsSnap, recTransactionsArchiveRef, "recTransactions");
        archiveDocsFromSnapshot(recurringRulesSnap, recurringRulesArchiveRef, "recurringTransactionRules");
        archiveDocsFromSnapshot(variableTransactionsSnap, variableTransactionsArchiveRef, "variableTransactions");
        archiveDocsFromSnapshot(ledgerTransactionsSnap, ledgerTransactionsArchiveRef, "ledger_transactions"); // New

        batches.push(currentBatch);

        // Commit all batches
        for (const b of batches) {
            await b.commit();
        }
    } catch (error) {
        console.error("Failed to delete and archive account data:", error);
        throw error;
    }
}

export function useLiveAccounts(userId: string | null, householdId?: string | null) {
    const [state, setState] = useState<FetchState<Account[]>>({
        data: null,
        loading: !!userId,
        error: null,
    });

    useEffect(() => {
        if (!userId) {
            setState({ data: null, loading: false, error: null });
            return;
        }

        setState(prevState => ({ ...prevState, loading: true, error: null }));

        let q;
        if (householdId) {
            q = query(
                collection(db, "accounts"),
                or(
                    where("userId", "==", userId),
                    where("householdId", "==", householdId)
                )
            );
        } else {
            q = query(collection(db, "accounts"), where("userId", "==", userId));
        }

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const accounts: Account[] = snapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        ...(data as Omit<Account, "id" | "createdAt" | "updatedAt" | "lastChecked">),
                        id: doc.id,
                        createdAt: typeof data.createdAt?.toMillis === 'function' ? data.createdAt.toMillis() : (data.createdAt || Date.now()),
                        updatedAt: typeof data.updatedAt?.toMillis === 'function' ? data.updatedAt.toMillis() : (data.updatedAt || Date.now()),
                        lastChecked: typeof data.lastChecked?.toMillis === 'function' ? data.lastChecked.toMillis() : (data.lastChecked || Date.now()),
                    };
                }).filter(account => {
                    // Always show own accounts
                    if (account.userId === userId) return true;
                    // Always show if user is an owner (future proof)
                    if (account.owners?.includes(userId)) return true;

                    // For others' accounts:
                    // If SHARED, show it
                    if (account.visibility === 'SHARED') return true;
                    // If no visibility set (legacy), assume PRIVATE if different user?
                    // actually, for legacy support with household, maybe default to SHARED or PRIVATE?
                    // Let's stick to strict: if not SHARED and not Owner, hide it.
                    // But if it was created before this feature, it has no visibility.
                    // If we assume default is SHARED for household accounts for backward compatibility...
                    // But legacy accounts don't have householdId either probably, unless migrated.
                    // If they have householdId and we fetched them, and they have NO visibility set, what do we do?
                    // Safe default is PRIVATE.
                    return false;
                });
                setState({ data: accounts, loading: false, error: null });
            },
            (error) => {
                console.error("Failed to fetch accounts:", error);
                setState({ data: null, loading: false, error: "Failed to fetch accounts data" });
            }
        );

        return () => unsubscribe();
    }, [userId, householdId]);

    return state;
}

export async function fixAccountData(accountId: string): Promise<void> {
    const accountRef = doc(db, "accounts", accountId);
    const accountSnap = await getDoc(accountRef);
    if (!accountSnap.exists()) throw new Error("Account not found");

    let calculatedBalance = 0;

    // Fetch all Ledger Transactions
    const q = query(collection(db, "ledger_transactions"), where("accountId", "==", accountId));
    const snap = await getDocs(q);

    const q2 = query(collection(db, "ledger_transactions"), where("transferAccountId", "==", accountId));
    const snap2 = await getDocs(q2);

    const processedIds = new Set<string>();

    const processDoc = (docSnap: any) => {
        if (processedIds.has(docSnap.id)) return;
        processedIds.add(docSnap.id);

        const data = docSnap.data();
        const amount = Number(data.amount) || 0;

        if (data.status !== 'POSTED') return;

        if (data.transferLeg) {
            if (data.accountId !== accountId) return;
            calculatedBalance += data.transferLeg === 'credit' ? amount : -amount;
            return;
        }

        if (data.accountId === accountId) {
            if (isOutflowTransaction(data)) {
                calculatedBalance -= amount;
            } else if (isInflowTransaction(data)) {
                calculatedBalance += amount;
            }
        } else if (data.transferAccountId === accountId) {
            if (isInflowTransaction(data)) {
                calculatedBalance += amount;
            }
        }
    };

    snap.docs.forEach(processDoc);
    snap2.docs.forEach(processDoc);

    // Explicit account-to-account transfers (Accounts page "Transfer" flow) live in their own
    // collection, not ledger_transactions — only `done` transfers affect balance.
    const transferOutQ = query(collection(db, "accountTransfers"), where("fromAccountId", "==", accountId));
    const transferInQ = query(collection(db, "accountTransfers"), where("toAccountId", "==", accountId));
    const [transferOutSnap, transferInSnap] = await Promise.all([getDocs(transferOutQ), getDocs(transferInQ)]);

    transferOutSnap.docs.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.done) calculatedBalance -= Number(data.amount) || 0;
    });
    transferInSnap.docs.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.done) calculatedBalance += Number(data.amount) || 0;
    });

    console.log(`[FixData] Recalculated for ${accountId}: Bal=${calculatedBalance}`);

    await updateDoc(accountRef, {
        balance: calculatedBalance,
        lastChecked: Timestamp.now()
    });
}
